import {
  BlockAddOnSubmissionPayload,
  BookingAddOnSelection,
  GetBookingAddOnsMenuPayload,
  ReadAddOnFormDefinitionPayload,
  ReadAddOnFormDefinitionResult,
  ResolveAddOnFormPayload,
  ResolveAddOnFormResult,
  ResolveAddOnRangesPayload,
  ResolveAddOnRangesResult,
  SubmitBookingAddOnsDto,
} from 'store/booking-add-ons/types';
import { createAction, createAsyncAction } from 'typesafe-actions';
import { FailureActionPayload } from 'store/errors/types';
import { Resource } from 'store/resource/types';

/*
 * Claim the slice for a booking. Dispatch on mount of the add-on step with the current
 * `resource_booking_details.id`. If the slice is currently held by a different booking its selection is
 * dropped — see the note on `BookingAddOnsState.bookingId`.
 */
export const startBookingAddOns = createAction('START_BOOKING_ADDONS')<string>();

/* Open the picker on the order the booking already carries. Separate from the claim because the seed
 * cannot be built until the menu has landed; applied once per booking, onto a slice claimed for it. */
export const seedBookingAddOns = createAction('SEED_BOOKING_ADDONS')<{
  bookingId: string;
  addOns: Array<BookingAddOnSelection>;
  formValues: Record<string, unknown> | null;
  /* Travels WITH the answers: the resolve drops values whose template it cannot match, and the seed
   * is the one writer that holds answers before the form owning them has resolved. */
  formTemplateUuid: string | null;
}>();

export const addBookingAddOn = createAction('ADD_BOOKING_ADDON')<BookingAddOnSelection>();

export const removeBookingAddOn = createAction('REMOVE_BOOKING_ADDON')<string>();

export const resetBookingAddOns = createAction('RESET_BOOKING_ADDONS')<undefined>();

/*
 * Hand the selection to the Service Request path. ⛔ NOT to the cart — the cart resolves exactly one
 * payment recipient, so it cannot carry both the landlord's room money and a vendor's.
 */
export const submitBookingAddOns = createAsyncAction(
  'SUBMIT_BOOKING_ADDONS_REQUEST',
  'SUBMIT_BOOKING_ADDONS_SUCCESS',
  'SUBMIT_BOOKING_ADDONS_FAILURE',
)<SubmitBookingAddOnsDto, void, FailureActionPayload>();

export const resetSubmitBookingAddOnsStatus = createAction('RESET_SUBMIT_BOOKING_ADDONS_STATUS')<undefined>();

/*
 * The submit was refused before any request was made, so this is not `submitBookingAddOns.failure`:
 * that status renders the generic retry message, which would sit beside the specific one.
 */
export const blockAddOnSubmission = createAction('BLOCK_ADDON_SUBMISSION')<BlockAddOnSubmissionPayload>();

/* An order was accepted for this booking, at this moment. Not derivable from
 * `submitBookingAddOns.status`, which is reset in the same handler and carries neither a booking nor a
 * time. The caller stamps the time so the reducer stays pure. */
/*
 * The money the card was showing as the tenant left it for the picker. Stamped here because this is
 * where it is known: the submit effect's own payment query is disabled until it sets
 * `payableBookingId`, and the card's cache entry is collected five minutes after the card unmounts.
 */
export const markAddOnsAmending = createAction('MARK_BOOKING_ADDONS_AMENDING')<{
  bookingId: string;
  paymentKey: string | null;
}>();

export const markAddOnsOrdered = createAction('MARK_BOOKING_ADDONS_ORDERED')<{
  bookingId: string;
  at: number;
  /* The money as it stood just BEFORE this submit. Both surfaces hold the server's answer against
   * it and withhold while it still matches. Recorded here because this is the last moment that
   * knows the pre-amend state. */
  paymentKey: string | null;
}>();

/* The service accepted a cancel for this booking, at this moment. Not `markAddOnsOrdered` with a
 * different time: the two stop on opposite conditions — an order's window closes when a payment block
 * appears, a cancel's when the state leaves `paid` for a refund. */
export const markAddOnsCancelled = createAction('MARK_BOOKING_ADDONS_CANCELLED')<{
  bookingId: string;
  at: number;
}>();

/*
 * RES-1057. Resolve the vendor's form for the chosen party: work-orders' owner-forms read, then the
 * definition fetch, as ONE action because neither half is useful alone.
 *
 * ⚠️ FAILS OPEN BY DESIGN. A rejection here means "no form applies", not "stop". The tenant's booking
 * completes without answers rather than being blocked behind a forms outage or a missing
 * `work_order_requestor` role — the vendor is the one who loses out, and that beats stranding a tenant
 * who has already paid for the room.
 */
export const resolveAddOnForm = createAsyncAction(
  'RESOLVE_ADDON_FORM_REQUEST',
  'RESOLVE_ADDON_FORM_SUCCESS',
  'RESOLVE_ADDON_FORM_FAILURE',
)<ResolveAddOnFormPayload, ResolveAddOnFormResult, FailureActionPayload>();

/*
 * RES-1311. The per-product quantity range, read on step mount over the whole rendered menu.
 *
 * ⛔ ITS OWN TRIPLE, deliberately, even though it calls the same endpoint as `resolveAddOnForm`.
 * Reusing that one would set the form's status to pending and block the Continue button on a request
 * that has nothing to do with the form, and would collide on the single `pendingKey` that
 * `isStaleResult` compares against — the later dispatch wins and the other lane's success is dropped
 * in silence.
 *
 * ⚠️ FAILS OPEN, like the form lane: no range means an unbounded maximum and a floor of one, which is
 * exactly the behaviour before this existed. That is the only fallback an order already in a basket
 * can survive. It is also what a tenant without `CAN_MANAGE_TENANT_WORK_ORDERS` gets, since the read
 * is guarded on it.
 */
export const resolveAddOnRanges = createAsyncAction(
  'RESOLVE_ADDON_RANGES_REQUEST',
  'RESOLVE_ADDON_RANGES_SUCCESS',
  'RESOLVE_ADDON_RANGES_FAILURE',
)<ResolveAddOnRangesPayload, ResolveAddOnRangesResult, FailureActionPayload>();

/*
 * RES-1316. A stored answer set carries field keys and no question text, so the booking detail reads
 * the definition back to label them. Its own triple for the reason `resolveAddOnRanges` has one: the
 * form lane's status gates the add-on step's CTA, and borrowing it would block that button on a read
 * belonging to another screen. Fails open, so an unread definition means no answers rather than none
 * of the page.
 */
export const readAddOnFormDefinition = createAsyncAction(
  'READ_ADDON_FORM_DEFINITION_REQUEST',
  'READ_ADDON_FORM_DEFINITION_SUCCESS',
  'READ_ADDON_FORM_DEFINITION_FAILURE',
)<ReadAddOnFormDefinitionPayload, ReadAddOnFormDefinitionResult, FailureActionPayload>();

/* The renderer's `onChange`, which hands back the WHOLE values map rather than a delta. */
export const setAddOnFormValues = createAction('SET_ADDON_FORM_VALUES')<Record<string, unknown>>();

/* The renderer's `onValidate`. Reports every field, not just the touched ones. */
export const setAddOnFormValidity = createAction('SET_ADDON_FORM_VALIDITY')<boolean>();

/*
 * Drop the resolved form entirely. Dispatched when the selection no longer names any item a form
 * could apply to.
 *
 * ⛔ IT IS NOT MERELY A RESET. The empty-selection path issues no new request, so `switchMap` has
 * nothing to cancel and an earlier resolve is still in flight. Clearing `pendingKey` is what makes
 * that response unable to land afterwards — without it, a form the tenant just emptied reappears.
 */
export const clearAddOnForm = createAction('CLEAR_ADDON_FORM')<undefined>();

/* ACCESS-2591. The step's own room read, carrying the booking's start — see `BookingAddOnsState.menu`. */
export const getBookingAddOnsMenu = createAsyncAction(
  'GET_BOOKING_ADDONS_MENU_REQUEST',
  'GET_BOOKING_ADDONS_MENU_SUCCESS',
  'GET_BOOKING_ADDONS_MENU_FAILURE',
)<GetBookingAddOnsMenuPayload, { key: string; resource: Resource }, FailureActionPayload & { key: string }>();
